import * as React from 'react';
import { ModalContainer, LabelButton, Icon } from '@teamsnap/snap-ui';
import { AMPLITUDE_TSB_AD_TYPE, useAmplitude } from 'hooks/amplitude';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import { User } from 'auth';
import api from 'core/api';

export enum DemoType {
  CoachesPack = 'coaches_pack',
  Websites = 'websites',
  Tournaments = 'tournaments',
  Sponsorships = 'sponsorships',
}

export const TeamsnapAppFeatureCallout = ({ children }) => (
  <div className="sui-flex-1 sui-flex sui-items-center sui-border sui-rounded sui-border-neutral-background-strong sui-border-solid sui-p-2">
    <Icon name="check_circle" className="sui-text-positive-icon sui-mr-1" />
    <span className="sui-body-dense">{children}</span>
  </div>
);

export const TeamsnapAppConfirmationModal = ({ show, onClose, children }) => (
  <ModalContainer isOpen={show} closeButton={false} disableOverlayClose={true}>
    <div className="sui-min-h-full sui-flex sui-flex-col">
      <div className="sui-flex-1">{children}</div>
      <div className="sui-flex sui-justify-end sui-mt-6">
        <LabelButton
          onClick={onClose}
          sentiment="default"
          size="default"
          variantType="primary"
        >
          Done
        </LabelButton>
      </div>
    </div>
  </ModalContainer>
);

export const useTeamsnapAppAdUpsellActions = (
  user: User,
  demoType: DemoType,
  orgId: string
): [
  learnMore: (link: string) => void,
  sendEmailRequest: (onSuccess: () => void) => Promise<void>
] => {
  const { amplitudeTrack } = useAmplitude();

  const learnMore = React.useCallback((link: string) => {
    amplitudeTrack.tsbAdClickAction({
      adType: getAmplitudeEventType(demoType),
      upsell_action: 'learn_more',
    });
    window.open(link, '_blank');
  }, []);

  const sendEmailRequest = React.useCallback(async (onSuccess: () => void) => {
    try {
      const data = {
        user: {
          fullName: `${user.firstName} ${user.lastName}`,
          email: user.email,
        },
        demoType: demoType,
      };

      const response = await api.post(
        `/organizations/${orgId}/request_demo_notification`,
        data
      );
      if (response?.status === 200) {
        onSuccess();
        amplitudeTrack.tsbAdClickAction({
          adType: getAmplitudeEventType(demoType),
          upsell_action: 'request_demo',
        });
      }
    } catch (e) {
      reactiveFeedbackMessage({
        message:
          'Oops! Failed to send email. Please try again or contact TeamSnap support for help.',
        isError: true,
        autoclose: true,
      });
    }
  }, []);

  return [learnMore, sendEmailRequest];
};

const getAmplitudeEventType = (demoType: DemoType) => {
  switch (demoType) {
    case DemoType.CoachesPack:
      return AMPLITUDE_TSB_AD_TYPE.COACHES_PACK;
    case DemoType.Websites:
      return AMPLITUDE_TSB_AD_TYPE.WEBSITE;
    case DemoType.Tournaments:
      return AMPLITUDE_TSB_AD_TYPE.TOURNAMENT;
    case DemoType.Sponsorships:
      return AMPLITUDE_TSB_AD_TYPE.SPONSORSHIP;
  }
};
